import { PlatformLocation } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA} from '@angular/material/legacy-dialog';
import { filter, flatMap } from 'rxjs/operators';
import { BaseComponent } from '../../shared/base-components/base.component';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { CustomerContact } from '../models/customer-contact.model';
import { ContactMethodType } from '../models/enums/contact-method-type.enum';
import { CustomerManageService } from '../shared/customer-manage.service';

export class ContactContext {
    constructor(
        public contactId: number,
        public customerNumberId: number
    ) {
    }
}

@Component({
    selector: 'customer-contact-modal',
    templateUrl: './customer-contact-modal.html'
})
export class CustomerContactModalComponent extends BaseComponent {
    public contactId: number;
    public customerNumberId: number;
    public customerContactTypes: any[];
    public showButtonLoading = false;
    public contactForm: UntypedFormGroup;
    public isEmail = false;
    public isMobile = false;
    public isPhone = false;
    public isFax = false;

    private contact: CustomerContact;
    private contactTypes = new ContactMethodType();

    constructor(
        public location: PlatformLocation,
        public service: CustomerManageService,
        public dialogRef: MatDialogRef<CustomerContactModalComponent>,
        @Optional()
        @Inject(MAT_DIALOG_DATA)
        public data: ContactContext,
        public modalService: MvdModalService,
        formBuilder: UntypedFormBuilder
    ) {
        super();
        this.customerNumberId = this.data.customerNumberId;
        this.contactId = this.data.contactId;
        this.contactForm = formBuilder.group({
            contactMethodTypeId: [null, Validators.required],
            customerContactValue: [''],
            isCorrespondenceEnabled: [null],
            isPrimary: [null],
            isPreferredContactMethod: [null],
            isInvalid: [null]
        });
        location.onPopState(() => {
            this.dialogRef.close();
        });
    }

    public ngOnInit(): void {
        this.customerContactTypes = this.contactTypes.GetActive();

        if (this.contactId != 0) {
            this.initContact();
        }
    }

    public updateComponentForContactType(): void {
        this.isEmail = false;
        this.isMobile = false;
        this.isPhone = false;
        this.isFax = false;
        let contactMethodTypeId = this.contactForm.get('contactMethodTypeId').value;

        if ((contactMethodTypeId == this.contactTypes.PersonalCell.id) ||
            (contactMethodTypeId == this.contactTypes.BusinessCell.id)
        ) {
            this.isMobile = true;
            this.isPhone = true;
        } else if ((contactMethodTypeId == this.contactTypes.HomeLandline.id) ||
            (contactMethodTypeId == this.contactTypes.BusinessLandline.id)
        ) {
            this.isPhone = true;
        } else if ((contactMethodTypeId == this.contactTypes.PersonalEmail.id) ||
            (contactMethodTypeId == this.contactTypes.BusinessEmail.id) ||
            (contactMethodTypeId == this.contactTypes.AlternativeEmail.id)
        ) {
            this.isEmail = true;
        } else if ((contactMethodTypeId == this.contactTypes.BusinessFax.id) ||
            (contactMethodTypeId == this.contactTypes.PersonalFax.id)
        ) {
            this.isFax = true;
        }
    }

    private initContact(): void {
        this.service.getCustomerContact(this.contactId)
            .subscribe(result => {
                this.contact = result;
                this.contactForm.get('contactMethodTypeId').setValue(result.contactMethodTypeId);
                this.contactForm.get('customerContactValue').setValue(result.customerContactValue);
                this.contactForm.get('isCorrespondenceEnabled').setValue(result.isCorrespondenceEnabled);
                this.contactForm.get('isPrimary').setValue(result.isPrimary);
                this.contactForm.get('isPreferredContactMethod').setValue(result.isPreferredContactMethod);
                this.contactForm.get('isInvalid').setValue(result.isInvalid);
            },
            e => this.handleValidationResult(e),
            () => {
                if (this.contact.isInvalid) {
                    this.contactForm.get('isPrimary').disable();
                }

                this.updateComponentForContactType();
            });
    }

    public cancel(): void {
        this.dialogRef.close();
    }

    public invalidChanged(event: any): void {
        let isPrimaryControl = this.contactForm.get('isPrimary');

        if (event.target.checked) {
            isPrimaryControl.setValue(false);
            this.contactForm.get('isPrimary').disable();
        } else {
            this.contactForm.get('isPrimary').enable();
        }
    }

    public saveContact(): void {
        if (this.contactForm.valid) {
            this.showButtonLoading = true;
            let model = this.getModel();

            this.service.saveContact(model)
                .subscribe(_ => {
                    this.showButtonLoading = false;
                    this.dialogRef.close(true);
                },
                e => {
                    this.showButtonLoading = false;
                    this.handleValidationResult(e);
                });
        }
    }

    public deleteContact(): void {
        if (!this.contact || !this.contact.id) {
            return;
        }

        if (this.contact && this.contact.id) {
            let contactMethodTypeId = this.contactForm.get('contactMethodTypeId').value;
            let contactId = this.contact.id;
            let itemType = this.contactTypes.FromId(contactMethodTypeId).displayValue;

            this.modalService.confirm()
                .title('Warning')
                .body(`Deleting ${itemType} will remove it from the active customer record view. This information will remain within customer record history.\n\nAre you sure you want to delete this information?`)
                .okBtn('Delete')
                .cancelBtn('Cancel')
                .open()
                .afterClosed()
                .pipe(
                    filter(result => !!result),
                    filter(() => contactId > 0),
                    flatMap(() => this.service.deleteContact(contactId))
                ).subscribe(() => this.dialogRef.close(true));
        }
    }

    private getModel = (): CustomerContact => {
        let model = new CustomerContact();

        if (null != this.contact) {
            Object.assign(model, this.contact);
        }

        model.id = this.contactId;
        model.customerNumberId = this.customerNumberId;
        model.contactMethodTypeId = this.contactForm.get('contactMethodTypeId').value || 0;
        model.customerContactValue = this.contactForm.get('customerContactValue').value.trim();
        model.isCorrespondenceEnabled = true == this.contactForm.get('isCorrespondenceEnabled').value;
        model.isPrimary = true == this.contactForm.get('isPrimary').value;
        model.isPreferredContactMethod = true == this.contactForm.get('isPreferredContactMethod').value;
        model.isInvalid = true == this.contactForm.get('isInvalid').value;

        return model;
    };
}
